import React from 'react';
import { RouterProvider } from 'react-router';
import { observer } from 'mobx-react-lite';
import { WithWaitForFeature } from '@monorepo/feature-flag/client';
import { AppLoadingSpinner } from '@monorepo/react-components';
import { getRoot } from '../helpers/getters.store';
import { usePrivateRoutes, usePublicRoutes } from '../hooks';
import { EnvService } from '../services/core/env-service';

const { application } = EnvService;

interface RouterHandlerProps {
  basename: string;
}

/**
 * Provider for authenticated users.
 * Only mounted when logged in - ensures private router created only when needed.
 */
const PrivateRouterProvider: React.FC<RouterHandlerProps> = ({ basename }) => {
  const routes = usePrivateRoutes(basename);
  return (
    <React.Suspense fallback={<AppLoadingSpinner application={application} />}>
      <RouterProvider router={routes} />
    </React.Suspense>
  );
};

/**
 * Provider for unauthenticated users.
 * Only mounted when logged out - ensures public router created only when needed.
 */
const PublicRouterProvider: React.FC<RouterHandlerProps> = ({ basename }) => {
  const routes = usePublicRoutes(basename);
  return (
    <React.Suspense fallback={<AppLoadingSpinner application={application} />}>
      <RouterProvider router={routes} />
    </React.Suspense>
  );
};

/**
 * Conditionally renders either private or public router.
 * Ensures only ONE router is created, preventing:
 * - Dual loader execution
 * - localStorage race conditions
 *
 * Wrapped with WithWaitForFeature to ensure feature flags (e.g. language
 * switching basename) are loaded before the router is created.
 */
const RouterHandler: React.FC<RouterHandlerProps> = ({ basename }) => {
  const { dataStore } = getRoot();
  const isLoggedIn = dataStore.userStore.isLoggedIn;

  return (
    <WithWaitForFeature
      fallback={<AppLoadingSpinner application={application} />}
      timeout={5000}
    >
      {isLoggedIn ? (
        <PrivateRouterProvider basename={basename} />
      ) : (
        <PublicRouterProvider basename={basename} />
      )}
    </WithWaitForFeature>
  );
};

export default observer(RouterHandler);
